import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Cashpurr } from '@/components/ui/cashpurr';
import { SubscriptionGuard } from '@/components/ui/subscription-guard';
import { 
  BookOpen, Copy, CheckCircle, Search, 
  Loader2, Sparkles, MessageSquare, TrendingUp
} from 'lucide-react';

interface Prompt {
  id: string;
  title: string;
  description: string;
  prompt_text: string;
  category: string;
  is_active: boolean;
  created_at: string;
}

const PromptLibrary: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);

  const categories = [
    { value: 'all', label: 'All Prompts' },
    { value: 'research', label: 'Research' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'validation', label: 'Validation' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'content', label: 'Content' }
  ];

  useEffect(() => {
    if (user) {
      loadPrompts();
    }
  }, [user]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('prompt_library')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // If no data from database, use dummy data
      if (!data || data.length === 0) {
        const dummyPrompts: Prompt[] = [
          {
            id: '1',
            title: 'Market Research for Your Idea',
            description: 'Use this to analyze your idea\'s market fit with ChatGPT.',
            prompt_text: 'Act as a market research expert. Given my startup idea [insert idea], analyze its market fit for [target audience]. Provide data-driven insights on demand, competition, and growth potential, with 3 actionable steps to validate it. Keep it concise and realistic.',
            category: 'research',
            is_active: true,
            created_at: new Date().toISOString(),
          },
          {
            id: '2',
            title: 'Customer Validation Strategy',
            description: 'Create a customer validation plan for your startup.',
            prompt_text: 'As a startup validation expert, help me create a customer validation strategy for [my product/service]. I want to validate [specific assumption] with [target customer segment]. Provide a step-by-step plan including interview questions, validation metrics, and success criteria. Keep it practical and actionable.',
            category: 'validation',
            is_active: true,
            created_at: new Date().toISOString(),
          },
          {
            id: '3',
            title: 'Marketing Copy Generator',
            description: 'Generate compelling marketing copy for your product.',
            prompt_text: 'You are a conversion copywriter. Help me write compelling marketing copy for [product/service] that targets [audience]. The main benefit is [benefit] and the key differentiator is [differentiator]. Create a headline, subheadline, and 3 key benefit statements. Make it persuasive but honest.',
            category: 'marketing',
            is_active: true,
            created_at: new Date().toISOString(),
          },
          {
            id: '4',
            title: 'Competitive Analysis Framework',
            description: 'Analyze your competition systematically.',
            prompt_text: 'As a competitive intelligence expert, help me analyze [competitor name] for my [product/service]. Focus on their value proposition, target market, pricing strategy, and key strengths/weaknesses. Then provide 3 strategic recommendations for how I can differentiate and compete effectively.',
            category: 'strategy',
            is_active: true,
            created_at: new Date().toISOString(),
          },
          {
            id: '5',
            title: 'Content Marketing Plan',
            description: 'Create a content marketing strategy for your brand.',
            prompt_text: 'You are a content marketing strategist. Help me create a 30-day content plan for [my business/brand] targeting [audience]. I want to establish thought leadership in [industry/topic]. Provide content themes, post types, and specific topic ideas. Include both educational and promotional content.',
            category: 'content',
            is_active: true,
            created_at: new Date().toISOString(),
          }
        ];
        setPrompts(dummyPrompts);
      } else {
        setPrompts(data);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
      // Use dummy data on error
      const dummyPrompts: Prompt[] = [
        {
          id: '1',
          title: 'Market Research for Your Idea',
          description: 'Use this to analyze your idea\'s market fit with ChatGPT.',
          prompt_text: 'Act as a market research expert. Given my startup idea [insert idea], analyze its market fit for [target audience]. Provide data-driven insights on demand, competition, and growth potential, with 3 actionable steps to validate it. Keep it concise and realistic.',
          category: 'research',
          is_active: true,
          created_at: new Date().toISOString(),
        },
        {
          id: '2',
          title: 'Customer Validation Strategy',
          description: 'Create a customer validation plan for your startup.',
          prompt_text: 'As a startup validation expert, help me create a customer validation strategy for [my product/service]. I want to validate [specific assumption] with [target customer segment]. Provide a step-by-step plan including interview questions, validation metrics, and success criteria. Keep it practical and actionable.',
          category: 'validation',
          is_active: true,
          created_at: new Date().toISOString(),
        },
        {
          id: '3',
          title: 'Marketing Copy Generator',
          description: 'Generate compelling marketing copy for your product.',
          prompt_text: 'You are a conversion copywriter. Help me write compelling marketing copy for [product/service] that targets [audience]. The main benefit is [benefit] and the key differentiator is [differentiator]. Create a headline, subheadline, and 3 key benefit statements. Make it persuasive but honest.',
          category: 'marketing',
          is_active: true,
          created_at: new Date().toISOString(),
        },
        {
          id: '4',
          title: 'Competitive Analysis Framework',
          description: 'Analyze your competition systematically.',
          prompt_text: 'As a competitive intelligence expert, help me analyze [competitor name] for my [product/service]. Focus on their value proposition, target market, pricing strategy, and key strengths/weaknesses. Then provide 3 strategic recommendations for how I can differentiate and compete effectively.',
          category: 'strategy',
          is_active: true,
          created_at: new Date().toISOString(),
        },
        {
          id: '5',
          title: 'Content Marketing Plan',
          description: 'Create a content marketing strategy for your brand.',
          prompt_text: 'You are a content marketing strategist. Help me create a 30-day content plan for [my business/brand] targeting [audience]. I want to establish thought leadership in [industry/topic]. Provide content themes, post types, and specific topic ideas. Include both educational and promotional content.',
          category: 'content',
          is_active: true,
          created_at: new Date().toISOString(),
        }
      ];
      setPrompts(dummyPrompts);
      toast({
        title: "Using Demo Data! 📚",
        description: "Showing sample prompts while we connect to the database.",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (promptText: string, promptId: string) => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopiedPrompt(promptId);
      toast({
        title: "Copied! 📋",
        description: "Prompt copied to clipboard. Ready to paste into ChatGPT!",
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast({
        title: "Copy Failed! 😿",
        description: "Failed to copy prompt. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'research': 'bg-blue-100 text-blue-800 border-blue-200',
      'marketing': 'bg-green-100 text-green-800 border-green-200',
      'validation': 'bg-purple-100 text-purple-800 border-purple-200',
      'strategy': 'bg-orange-100 text-orange-800 border-orange-200',
      'content': 'bg-pink-100 text-pink-800 border-pink-200',
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredPrompts = prompts.filter(prompt => {
    const matchesSearch = prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         prompt.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || prompt.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="space-y-6 text-center">
        <Cashpurr size="xl" mood="thinking" className="mx-auto" />
        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <Cashpurr size="lg" mood="excited" className="mx-auto mb-2" />
        <h1 className="text-3xl font-bold">Prompt Library</h1>
        <p className="text-muted-foreground">Pre-built ChatGPT prompts to supercharge your AI interactions</p>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {categories.map((category) => (
                <Button
                  key={category.value}
                  variant={selectedCategory === category.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.value)}
                  className="whitespace-nowrap"
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prompts Grid */}
      <div className="space-y-4">
        {filteredPrompts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No prompts found</p>
              <p className="text-sm">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'More prompts coming soon! Check back for updates.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredPrompts.map((prompt) => (
              <Card key={prompt.id} className="relative">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center space-x-2">
                        <Sparkles className="w-5 h-5 text-yellow-500" />
                        <span>{prompt.title}</span>
                      </CardTitle>
                      <CardDescription className="mt-2">
                        {prompt.description}
                      </CardDescription>
                    </div>
                    <Badge className={getCategoryColor(prompt.category)}>
                      {prompt.category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-muted p-3 rounded-md">
                    <p className="text-sm font-mono whitespace-pre-wrap">
                      {prompt.prompt_text}
                    </p>
                  </div>
                  <div className="flex justify-between items-center">
                    <Button
                      onClick={() => copyToClipboard(prompt.prompt_text, prompt.id)}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      {copiedPrompt === prompt.id ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy Prompt</span>
                        </>
                      )}
                    </Button>
                    <div className="text-xs text-muted-foreground">
                      {new Date(prompt.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Usage Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5" />
            <span>How to Use These Prompts</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                1
              </div>
              <p className="text-sm font-medium">Copy Prompt</p>
              <p className="text-xs text-muted-foreground">Click the copy button to get the prompt text</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                2
              </div>
              <p className="text-sm font-medium">Paste in ChatGPT</p>
              <p className="text-xs text-muted-foreground">Open ChatGPT and paste the prompt</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
                3
              </div>
              <p className="text-sm font-medium">Customize & Send</p>
              <p className="text-xs text-muted-foreground">Replace placeholders with your specific details</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon */}
      <Card>
        <CardContent className="text-center py-6">
          <div className="flex items-center justify-center space-x-3 mb-3">
            <Cashpurr size="md" mood="excited" />
            <p className="text-lg font-medium">
              More prompts coming soon! 🚀
            </p>
          </div>
          <p className="text-muted-foreground">
            We're constantly adding new prompts for marketing, validation, and strategy. 
            Check back regularly for fresh AI-powered insights!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const PromptLibraryPage: React.FC = () => {
  return (
    <SubscriptionGuard feature="prompt_library">
      <PromptLibrary />
    </SubscriptionGuard>
  );
};

export default PromptLibraryPage;
